// AudioPlayer.ts

/**
 * Defines the mapping between the logical sound name and its MP3 filename.
 * This list should match the files in public/sounds/*.mp3
 */
const MP3_SOUND_MAP = {
    move: 'move.mp3',
    capture: 'capture.mp3',
    castle: 'castle.mp3',
    promotion: 'promote.mp3',
    check: 'check.mp3',
    gameEnd: 'game-end.mp3',
    gameStart: 'game-start.mp3',
    illegal: 'illegal.mp3',
    lowTime: 'low-time.mp3',
    // You can add more sounds here if needed
};

export type MP3SoundType = keyof typeof MP3_SOUND_MAP;

/**
 * AudioPlayer class
 *
 * Handles loading, decoding, and playing custom MP3 sound files using the Web Audio API.
 */
export class AudioPlayer {
    private audioContext: AudioContext;
    private bufferCache: Map<MP3SoundType, AudioBuffer> = new Map();

    constructor(audioContext: AudioContext) {
        this.audioContext = audioContext;
    }

    /**
     * Loads and decodes all MP3 files into AudioBuffers.
     */
    async loadMP3Sounds(): Promise<void> {
        const promises = Object.entries(MP3_SOUND_MAP).map(async ([key, filename]) => {
            const url = `/sounds/${filename}`;
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
                this.bufferCache.set(key as MP3SoundType, audioBuffer);
                console.log(`Successfully loaded MP3: ${filename}`);
            } catch (error) {
                console.error(`Failed to load or decode MP3: ${url}`, error);
            }
        });
        await Promise.all(promises);
    }

    /**
     * Plays a loaded sound buffer.
     * @param sound The sound type to play.
     * @param volume The volume gain (0.0 to 1.0).
     */
    play(sound: MP3SoundType, volume: number): void {
        const audioBuffer = this.bufferCache.get(sound);

        if (!audioBuffer) {
            console.warn(`MP3 sound not loaded: ${sound}`);
            return;
        }

        // 1. Create a buffer source
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;

        // 2. Create a Gain Node for volume control
        const gainNode = this.audioContext.createGain();
        gainNode.gain.value = volume;

        // 3. Connect nodes: Source -> Gain -> Destination
        source.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        // 4. Start playback
        source.start(0);
    }
}